import { useState } from 'react';
import { motion } from 'framer-motion';
import Images from '../../../utils/Images';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import authService from '../../../Services/AuthService';

export default function ForgotPassword() {
  const [method, setMethod] = useState('email');
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [userSlug, setUserSlug] = useState('');
  const navigate = useNavigate();

  // Phone validation: 233 prefix = 12 digits total, 0 prefix = 10 digits total
  const isValidPhone = (phone) => {
    const digits = phone.replace(/\D/g, '');
    if (digits.startsWith('233')) {
      return digits.length === 12;
    } else if (digits.startsWith('0')) {
      return digits.length === 10;
    }
    return false;
  };

  const canSubmit = (method === 'email' && /.+@.+\..+/.test(value)) || (method === 'phone' && isValidPhone(value));

  // Format phone number for API payload - always use 233 prefix
  const formatPhoneForAPI = (phone) => {
    const digits = phone.replace(/\D/g, '');
    if (digits.startsWith('233')) {
      return digits; 
    } else if (digits.startsWith('0')) {
      return '233' + digits.substring(1);
    }
    return digits;
  };

  const submit = async () => {
    if (!canSubmit || loading) return;
    setLoading(true);
    setError('');
    
    try {
      const payload = {
        email_or_phone: method === 'phone' ? formatPhoneForAPI(value) : value,
        channel: method
      };
      
      const response = await authService.forgotPassword(payload);
      
      if (response.status) {
        setSuccess(true);
        setResendCooldown(30);
        startCooldownTimer();
        // Store the slug from the response for password reset
        if (response.data?.data?.data?.slug) {
          setUserSlug(response.data.data.data.slug);
        }
      } else {
        setError(response.message || 'Failed to send reset code');
      }
    } catch (error) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resendOTP = async () => {
    if (!canSubmit || resendLoading || resendCooldown > 0) return;
    setResendLoading(true);
    setError('');
    
    try {
      const payload = {
        email_or_phone: method === 'phone' ? formatPhoneForAPI(value) : value,
        channel: method
      };
      
      const response = await authService.forgotPassword(payload);
      
      if (response.status) {
        setSuccess(true);
        setResendCooldown(60);
        startCooldownTimer();
        // Store the slug from the response for password reset
        if (response.data?.data?.data?.slug) {
          setUserSlug(response.data.data.data.slug);
        }
      } else {
        setError(response.message || 'Failed to resend reset code');
      }
    } catch (error) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  const startCooldownTimer = () => {
    const timer = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  return (
    <div className="relative h-screen overflow-hidden">
      <img src="https://images.unsplash.com/photo-1557804506-669a67965ba0?q=80&w=1600&auto=format&fit=crop" alt="bg" className="object-cover absolute inset-0 w-full h-full" />
      <div className="absolute inset-0 bg-black/60 pointer-events-none" />

      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="flex absolute top-0 left-0 right-0 z-10 justify-center pt-6">
        <img src={Images.logo_navbar} alt="TripSecure" className="h-10" />
      </motion.div>
      <button onClick={() => navigate(-1)} className="flex absolute top-4 left-4 z-20 items-center gap-2 px-3 py-2 text-xs font-medium text-white/90 rounded-lg border border-white/20 bg-white/10 backdrop-blur hover:bg-white/20">
        <ArrowLeft size={14} /> Back
      </button>

      <div className="flex relative z-10 justify-center items-center h-screen p-6">
        <motion.div initial={{ opacity: 0, y: 20, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.6 }} className="p-6 w-full max-w-md rounded-3xl shadow-2xl bg-white/95">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.4 }}>
            <h2 className="text-lg font-semibold text-center">Reset Password</h2>
            <p className="mt-1 text-xs text-center text-gray-500">
              {success ? `Code sent to your ${method}! Check your ${method === 'email' ? 'email inbox' : 'phone'} for the 4-digit code.` : 'Enter your email or phone to receive a 4-digit code'}
            </p>
          </motion.div>

          <div className="mt-6 space-y-4">
            <div className="grid grid-cols-2 gap-2 p-1 bg-gray-100 rounded-lg">
              <button 
                onClick={() => { setMethod('email'); setValue(''); setSuccess(false); setError(''); }} 
                disabled={success}
                className={`px-3 py-2 text-sm rounded-md ${method === 'email' ? 'bg-white shadow font-semibold' : 'text-gray-600'} ${success ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                Email
              </button>
              <button 
                onClick={() => { setMethod('phone'); setValue(''); setSuccess(false); setError(''); }} 
                disabled={success}
                className={`px-3 py-2 text-sm rounded-md ${method === 'phone' ? 'bg-white shadow font-semibold' : 'text-gray-600'} ${success ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                Phone
              </button>
            </div>

            <div>
              <input
                type={method === 'email' ? 'email' : 'tel'}
                placeholder={method === 'email' ? 'you@example.com' : '0555 123 456 or 233555123456'}
                value={value}
                onChange={(e) => {
                  setValue(e.target.value);
                  setError(''); // Clear error when user types
                }}
                disabled={success}
                className={`px-4 py-3 w-full text-sm rounded-xl border border-gray-200 outline-none bg-white/95 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100 ${success ? 'opacity-50 cursor-not-allowed' : ''}`}
              />
              <p className="mt-1 text-[11px] text-gray-500">
                {success ? `Code sent to ${value}` : method === 'phone' ? 'Enter your phone number (10 digits starting with 0 or 12 digits starting with 233)' : `We will send the code to this ${method}.`}
              </p>
              {error && (
                <p className="mt-2 text-xs text-red-500">{error}</p>
              )}
              {success && (
                <p className="mt-2 text-xs text-green-600">✓ Code sent successfully!</p>
              )}
            </div>

            {!success ? (
              <motion.button
                onClick={submit}
                disabled={!canSubmit || loading}
                whileHover={{ scale: canSubmit && !loading ? 1.02 : 1 }}
                whileTap={{ scale: canSubmit && !loading ? 0.98 : 1 }}
                className="overflow-hidden relative px-4 py-3 w-full text-sm font-semibold text-white bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl shadow-lg hover:from-emerald-600 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-400/50 focus:ring-offset-2 disabled:opacity-50"
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Sending...
                  </div>
                ) : (
                  'Send Code'
                )}
              </motion.button>
            ) : (
              <div className="space-y-3">
                <motion.button
                  onClick={() => navigate('/forgot-password/reset', { state: { method, contact: value, slug: userSlug } })}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="overflow-hidden relative px-4 py-3 w-full text-sm font-semibold text-white bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl shadow-lg hover:from-emerald-600 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-400/50 focus:ring-offset-2"
                >
                  Continue to Reset Password
                </motion.button>
                
                <motion.button
                  onClick={resendOTP}
                  disabled={resendLoading || resendCooldown > 0}
                  whileHover={{ scale: !resendLoading && resendCooldown === 0 ? 1.02 : 1 }}
                  whileTap={{ scale: !resendLoading && resendCooldown === 0 ? 0.98 : 1 }}
                  className="overflow-hidden relative px-4 py-3 w-full text-sm font-medium text-emerald-600 bg-emerald-50 rounded-xl border border-emerald-200 hover:bg-emerald-100 focus:outline-none focus:ring-2 focus:ring-emerald-400/50 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {resendLoading ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-emerald-300 border-t-emerald-600 rounded-full animate-spin"></div>
                      Resending...
                    </div>
                  ) : resendCooldown > 0 ? (
                    `Resend in ${resendCooldown}s`
                  ) : (
                    'Resend Code'
                  )}
                </motion.button>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}


